import ChevronDown20 from '@carbon/icons-react/es/chevron--down/20';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import { Dropdown } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { StatusUpdatePrivacy } from 'api/status-updates';
import { Button } from 'components/atoms/button';
import { Menu as MenuAtom } from 'components/atoms/dropdown';
import { HideIfNotAllowed } from 'components/organisms/permission-controller';
import { PermissionRuleName } from 'constant';
import { createStatusUpdate } from 'data/status-updates';
import { TemplateSourceType } from 'data/templates/api';
import { StatusUpdateTemplate } from 'data/templates/status-updates';
import { useGetAllMembersInProject } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useGetAllStatusUpdateTemplatesQuery } from 'queries/templates/status-update-templates';
import { getProjectStatusUpdatesDetailUrl } from 'utils/urls';
import { StatusTemplatesList } from './status-templates-list';

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  & button:not(:first-child) {
    margin-left: 1px;
  }
`;

const PrivateIcon = styled(ViewOff16)`
  margin-right: var(--spacing-03);
`;

export const NewUpdateButton = (): JSX.Element => {
  const { projectId: projectIdFromRoute, statusUpdateId } =
    useParams<{ projectId: string; statusUpdateId: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const projectId = parseInt(projectIdFromRoute, 10);
  const untitledTranslationText = useTranslation('untitled');
  const [showTemplateSearchModal, setShowTemplateSearchModal] = useState(false);
  const [isPrivate, setPrivate] = useState(false);

  const { data: templates = [] } = useGetAllStatusUpdateTemplatesQuery();
  const onCloseTemplateSearchModal = () => setShowTemplateSearchModal(false);

  const usersInProject = useGetAllMembersInProject({
    projectId,
    isPrivate,
  });

  useEffect(() => {
    if (statusUpdateId && showTemplateSearchModal) {
      setShowTemplateSearchModal(false);
    }
  }, [statusUpdateId, showTemplateSearchModal]);

  const createStatusUpdatePayload = useMemo(
    () => ({
      projectId,
      title: untitledTranslationText,
      collaborators: [],
      onSuccess: (update) => {
        if (update?.statusUpdateId) {
          history.push(
            getProjectStatusUpdatesDetailUrl(projectId, update?.statusUpdateId),
          );
        }
      },
    }),
    [history, projectId, untitledTranslationText],
  );

  const createNewStatusUpdate = useCallback(
    (type: StatusUpdatePrivacy) => {
      const payload = {
        ...createStatusUpdatePayload,
        private: type === StatusUpdatePrivacy.Private,
        toUsers: usersInProject,
      };

      dispatch(createStatusUpdate(payload));
      onCloseTemplateSearchModal();
    },
    [createStatusUpdatePayload, dispatch, usersInProject],
  );

  const openDrawer = () => {
    setShowTemplateSearchModal(true);
  };

  const onChooseTemplate = useCallback(
    (template: StatusUpdateTemplate, type: StatusUpdatePrivacy) => {
      onCloseTemplateSearchModal();
      const payload = {
        ...createStatusUpdatePayload,
        toUsers: usersInProject,
        private: type === StatusUpdatePrivacy.Private,
        source: {
          sourceType: TemplateSourceType.Template,
          sourceId: template.templateId,
        },
      };

      dispatch(createStatusUpdate(payload));
    },
    [createStatusUpdatePayload, dispatch, usersInProject],
  );

  const openInPrivate = () => {
    if (templates.length) {
      setPrivate(true);
      openDrawer();

      return;
    }

    createNewStatusUpdate(StatusUpdatePrivacy.Private);
  };

  const openInPublic = () => {
    if (templates.length) {
      setPrivate(false);
      openDrawer();

      return;
    }

    createNewStatusUpdate(StatusUpdatePrivacy.Shared);
  };

  const menu = (
    <MenuAtom>
      <MenuAtom.ItemWithPermission
        key={StatusUpdatePrivacy.Private}
        onClick={openInPrivate}
        data-cy="status_update_button_dropdown_private"
        ruleName={PermissionRuleName.StatusUpdateCreatePrivateAction}
      >
        <PrivateIcon />{' '}
        <FormattedMessage id="status_updates.new_update_private" />
      </MenuAtom.ItemWithPermission>
    </MenuAtom>
  );

  return (
    <HideIfNotAllowed ruleName={PermissionRuleName.StatusUpdateCreateAction}>
      <div>
        <ButtonGroup>
          <Button
            size="small"
            type="primary"
            data-cy="status_update_button"
            onClick={openInPublic}
          >
            <FormattedMessage id="status_updates.new_update_shared" />
          </Button>
          <HideIfNotAllowed
            ruleName={PermissionRuleName.StatusUpdateCreatePrivateAction}
          >
            <Dropdown overlay={menu}>
              <Button
                data-cy="status_update_button_dropdown"
                size="small"
                type="primary"
                icon={<ChevronDown20 />}
              />
            </Dropdown>
          </HideIfNotAllowed>
        </ButtonGroup>
        <StatusTemplatesList
          visible={showTemplateSearchModal}
          onClose={onCloseTemplateSearchModal}
          onChooseTemplate={onChooseTemplate}
          createUpdate={createNewStatusUpdate}
          isPrivate={isPrivate}
        />
      </div>
    </HideIfNotAllowed>
  );
};
